import { useCallback, useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import type { components } from "@dolfin/api-public";

type OutstandingResponse = components["schemas"]["OutstandingResponse"];
type OverdueResponse = components["schemas"]["OverdueResponse"];
type ReceivedResponse = components["schemas"]["ReceivedResponse"];

export interface ArStats {
  loading: boolean;
  error: string | null;
  outstanding: OutstandingResponse | null;
  overdue: OverdueResponse | null;
  received30: ReceivedResponse | null;
  received90: ReceivedResponse | null;
  refresh: () => Promise<void>;
}

export function useArStats(): ArStats {
  const { activeOrganisationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [outstanding, setOutstanding] = useState<OutstandingResponse | null>(null);
  const [overdue, setOverdue] = useState<OverdueResponse | null>(null);
  const [received30, setReceived30] = useState<ReceivedResponse | null>(null);
  const [received90, setReceived90] = useState<ReceivedResponse | null>(null);

  const fetchStats = useCallback(async () => {
    if (!activeOrganisationId) return;
    setLoading(true);
    setError(null);

    try {
      const [outstandingRes, overdueRes, received30Res, received90Res] = await Promise.all([
        api.GET("/v1/receivables/outstanding", {}),
        api.GET("/v1/receivables/overdue", {}),
        api.GET("/v1/receivables/received", { params: { query: { period: "Last30Days" } } }),
        api.GET("/v1/receivables/received", { params: { query: { period: "Last90Days" } } }),
      ]);

      if (outstandingRes.error || overdueRes.error || received30Res.error || received90Res.error) {
        setError("Failed to load receivables stats");
      }

      setOutstanding(outstandingRes.data ?? null);
      setOverdue(overdueRes.data ?? null);
      setReceived30(received30Res.data ?? null);
      setReceived90(received90Res.data ?? null);
    } catch (e) {
      console.error("Failed to load receivables stats:", e);
      setError("Failed to load receivables stats");
    } finally {
      setLoading(false);
    }
  }, [activeOrganisationId]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  return { loading, error, outstanding, overdue, received30, received90, refresh: fetchStats };
}
